import { Injectable, inject, signal } from '@angular/core';
import { AnalyticsEvent, AnalyticsService, ApiCallingV2Service, DropdownData, API_CONFIG } from '@zayan-erp/shared';
import { map, Observable, tap } from 'rxjs';
import {
  CreateDeposit,
  CreateDepositPayment,
  Deposit,
  DepositLots,
  DepositPayment,
  DepositSummary,
  DepositType,
  importDeposit
} from '../models/deposits.models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SalesApsService } from './sales-aps.service';

@Injectable({
  providedIn: 'root'
})
export class DepositService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);
  baseUrl = this.#apiConfig.baseUrlV2;

  dealId = signal<string | undefined>(undefined);
  lotId = signal<string | undefined>(undefined);

  addPayment: boolean = false;
  depositId: string | null = null;
  paymentId: string | null = null;
  addNewDeposits: boolean = false;

  deposits = signal<importDeposit[]>([]);

  form = new FormGroup({
    lotId: new FormControl<string | null>(null),
    dealId: new FormControl<string | null>(null),
    addendumId: new FormControl<string | null>(null),
    taskId: new FormControl<string | null>(null),
    depositTypeId: new FormControl<string | null>(null, [Validators.required]),
    depositDueDate: new FormControl<Date | null>(null, [Validators.required]),
    depositAmount: new FormControl<number | null>(null, [Validators.required]),
    notes: new FormControl<string | null>(null)
  });

  depositPaymentForm = new FormGroup({
    depositId: new FormControl<string | null>(null),
    paymentDate: new FormControl<Date | null>(null, [Validators.required]),
    paymentAmount: new FormControl<number | null>(null, [Validators.required]),
    referenceNo: new FormControl<string | null>(null),
    returned: new FormControl<boolean>(false),
    paymentImageIds: new FormControl<string[]>([])
  });

  readDepositsByLotId(lotId: string): Observable<Deposit[]> {
    return this.#http.getData<Deposit[]>(`${this.baseUrl}/deposits/lot/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readDepositsByDealId(dealId: string): Observable<Deposit[]> {
    return this.#http.getData<Deposit[]>(`${this.baseUrl}/deposits/deal/${dealId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readDepositById(id: string): Observable<Deposit> {
    return this.#http.getData<Deposit>(`${this.baseUrl}/deposits/${id}`, { showLoader: true, showMessage: false });
  }

  createDeposit(deposit: CreateDeposit[]): Observable<Deposit> {
    return this.#http
      .postData<Deposit>(
        `${this.baseUrl}/deposits`,
        { showLoader: true, buttonId: 'add-update-deposit', showMessage: true },
        { deposits: deposit }
      )
      .pipe(
        tap(() => {
          const first = deposit[0];
          const ctx = this.#salesAps.currentDealCtx({ lotId: first?.lotId, dealId: first?.dealId });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositCreated, {
            ...ctx,
            deposit_count: deposit.length
          });
        })
      );
  }

  updateDeposit(depositId: string, deposit: Deposit): Observable<Deposit> {
    return this.#http
      .putData<Deposit>(`${this.baseUrl}/deposits/${depositId}`, deposit, {
        showLoader: true,
        buttonId: 'add-update-deposit',
        showMessage: true
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx({ lotId: deposit.lotId, dealId: this.dealId() });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositUpdated, {
            ...ctx,
            deposit_id: depositId
          });
        })
      );
  }

  createDepositPayment(depositId: string, depositPayment: CreateDepositPayment): Observable<DepositPayment> {
    depositPayment.returned = depositPayment.returned ?? false;
    return this.#http
      .postData<DepositPayment>(
        `${this.baseUrl}/deposits/${depositId}/payment`,
        {
          showLoader: true,
          buttonId: 'add-update-deposit-payment',
          showMessage: true,
          message: 'Deposit payment added successfully'
        },
        depositPayment
      )
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx({ lotId: this.lotId(), dealId: this.dealId() });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositPaymentRecorded, {
            ...ctx,
            deposit_id: depositId,
            payment_amount: depositPayment.paymentAmount
          });
        })
      );
  }

  updateDepositPayment(
    depositId: string,
    paymentId: string,
    depositPayment: CreateDepositPayment
  ): Observable<DepositPayment> {
    return this.#http
      .putData<DepositPayment>(`${this.baseUrl}/deposits/${depositId}/payment/${paymentId}`, depositPayment, {
        showLoader: true,
        buttonId: 'add-update-deposit-payment',
        showMessage: true,
        message: 'Deposit payment updated successfully'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx({ lotId: this.lotId(), dealId: this.dealId() });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositPaymentUpdated, {
            ...ctx,
            deposit_id: depositId,
            payment_id: paymentId
          });
        })
      );
  }

  getDepositTypes(): Observable<DropdownData[]> {
    return this.#http
      .getData<DepositType[]>(`${this.baseUrl}/deposits/types`, { showLoader: true, showMessage: false })
      .pipe(
        map((data: DepositType[] | null | undefined) =>
          (data ?? []).map((item: DepositType) => ({
            id: item.id,
            firstLabel: item.name,
            secondLabel: ''
          }))
        )
      );
  }

  getSummary(lotId: string): Observable<DepositSummary[]> {
    return this.#http.getData<DepositSummary[]>(`${this.baseUrl}/deposits/lot/${lotId}/summary`, {
      showLoader: true,
      showMessage: false
    });
  }

  getSummaryByDeal(dealId: string): Observable<DepositSummary[]> {
    return this.#http.getData<DepositSummary[]>(`${this.baseUrl}/deposits/deal/${dealId}/summary`, {
      showLoader: false,
      showMessage: false
    });
  }

  adoptDeposits(dealId: string, depositIds: string[]): Observable<unknown> {
    return this.#http.postData<unknown>(
      `${this.baseUrl}/deposits/deal/${dealId}/adopt`,
      { showLoader: true, showMessage: true, showError: false },
      { depositIds }
    );
  }

  deleteDeposit(depositId: string): Observable<void> {
    return this.#http
      .deleteData<void>(`${this.baseUrl}/deposits/${depositId}`, {
        showLoader: true,
        showMessage: true
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx({ lotId: this.lotId(), dealId: this.dealId() });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositDeleted, { ...ctx, deposit_id: depositId });
        })
      );
  }

  deleteDepositPayment(paymentId: string): Observable<void> {
    return this.#http
      .deleteData<void>(`${this.baseUrl}/deposits/payment/${paymentId}`, {
        showLoader: true,
        showMessage: true
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx({ lotId: this.lotId(), dealId: this.dealId() });
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DepositPaymentDeleted, { ...ctx, payment_id: paymentId });
        })
      );
  }

  downloadReceipt(paymentId: string, downloadFileName?: string): Observable<void> {
    return this.#http.downloadReportFile(
      `${this.baseUrl}/deposits/payment/${paymentId}/receipt`,
      {
        showLoader: true,
        showMessage: true
      },
      undefined,
      downloadFileName
    );
  }

  dealsByDealId(dealId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<DepositLots[]>(`${this.baseUrl}/deposits/deals/${dealId}/lots`, { showLoader: true, showMessage: false })
      .pipe(
        map((lots: DepositLots[] | null | undefined) =>
          (lots ?? []).map((item: DepositLots) => ({
            id: item.id,
            firstLabel: item.lotNo,
            secondLabel: item.descr
          }))
        )
      );
  }

  earnestByLotId(lotId: string): Observable<importDeposit[]> {
    return this.#http.getData<importDeposit[]>(`${this.baseUrl}/deposits/earnest/lot/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  generateDeposits(
    projectId: string,
    lotId: string,
    dealId: string,
    depScehdulId: string,
    confirmDuplicate = false
  ): Observable<importDeposit[]> {
    return this.#http.postData<importDeposit[]>(
      `${this.baseUrl}/deposit-schedules/generate`,
      { showLoader: true, showMessage: false, showError: false },
      { projectId, lotId, dealId, depositScheduleId: depScehdulId, confirmDuplicate }
    );
  }
}
